/** Mirrors `pricemodifier.PriceModifier`, backend/core/omni/domain/pricemodifier/price_modifier.go. */
export type PriceModifier = {
  active: boolean;
  title: string;
  kind: PriceModifierKind;
  schema: PriceModifierSchema;
  relativeBase: number;
  relativeRate: number;
  value: number;
};

/** Mirrors `pricemodifier.Kind`, backend/core/omni/domain/pricemodifier/price_modifier.go. */
export enum PriceModifierKind {
  Discount = "discount",
  Surcharge = "surcharge",
}

/** Mirrors `pricemodifier.Schema`, backend/core/omni/domain/pricemodifier/price_modifier.go. */
export enum PriceModifierSchema {
  Relative = "relative",
  Absolute = "absolute",
}

/** newPriceModifier returns an active, relative discount without amount. */
export function newPriceModifier(): PriceModifier {
  return {
    active: true,
    title: "",
    kind: PriceModifierKind.Discount,
    schema: PriceModifierSchema.Relative,
    relativeBase: 0,
    relativeRate: 0,
    value: 0,
  };
}

/**
 * priceModifierAmount returns the unsigned amount the user enters: the
 * rate of a relative, the value of an absolute modifier.
 */
export function priceModifierAmount(m: PriceModifier): number {
  return Math.abs(
    m.schema === PriceModifierSchema.Relative ? m.relativeRate : m.value,
  );
}

/**
 * withPriceModifierAmount returns m with the unsigned amount set as the
 * rate (relative) or value (absolute). The backend stores discounts
 * negative and surcharges positive, so the sign follows the kind.
 */
export function withPriceModifierAmount(
  m: PriceModifier,
  amount: number,
): PriceModifier {
  const signed =
    m.kind === PriceModifierKind.Discount
      ? -Math.abs(amount)
      : Math.abs(amount);
  return m.schema === PriceModifierSchema.Relative
    ? { ...m, relativeRate: signed }
    : { ...m, value: signed };
}

/** withPriceModifierKind returns m of kind, its amount re-signed. */
export function withPriceModifierKind(
  m: PriceModifier,
  kind: PriceModifierKind,
): PriceModifier {
  const sign = kind === PriceModifierKind.Discount ? -1 : 1;
  return {
    ...m,
    kind,
    relativeRate: sign * Math.abs(m.relativeRate),
    value: sign * Math.abs(m.value),
  };
}

/**
 * withPriceModifierSchema returns m of schema, its amount moved to the
 * field of the schema. The backend calculates the value of a relative
 * modifier from its rate.
 */
export function withPriceModifierSchema(
  m: PriceModifier,
  schema: PriceModifierSchema,
): PriceModifier {
  const amount = priceModifierAmount(m);
  return withPriceModifierAmount(
    { ...m, schema, relativeRate: 0, value: 0 },
    amount,
  );
}
